import { translate, translateRich, useLocale } from "../../localization";
import React, { useMemo, useState } from "react";
import { AppButton } from "../ui";
import "./ActivityByTime.scss";

/**
 * ActivityByTime
 * 
 * Shows when students are most active - by day of week and hour of day.
 * Helps teachers plan when to be available for support.
 * 
 * Decision it enables: "When are my students most active?"
 */
export default function ActivityByTime({ 
  sessions = [],
  detailedSessions = []
}) {
  const locale = useLocale();
  const [view, setView] = useState("day"); // "day" or "hour"

  const timeAnalysis = useMemo(() => {
    // Combine sessions and detailed sessions for timestamps
    const allTimestamps = [];

    sessions.forEach(session => {
      const timestamp = session.startTime || session.timestamp || session.dateCreated;
      if (timestamp) {
        allTimestamps.push(new Date(timestamp));
      }
    });

    detailedSessions.forEach(session => {
      (session.events || []).forEach(event => {
        const timestamp = event.sk || event.dateCreated || event.timestamp;
        if (timestamp) {
          allTimestamps.push(new Date(timestamp));
        }
      });
    });

    // Filter out invalid dates
    const validTimestamps = allTimestamps.filter(d => !isNaN(d.getTime()));

    if (validTimestamps.length === 0) {
      return { byDay: [], byHour: [], peakDay: null, peakHour: null, totalEvents: 0 };
    }

    // Count by day of week
    const dayNames = [translate("Sunday"), translate("Monday"), translate("Tuesday"), translate("Wednesday"), translate("Thursday"), translate("Friday"), translate("Saturday")];
    const dayShort = [translate("Sun"), translate("Mon"), translate("Tue"), translate("Wed"), translate("Thu"), translate("Fri"), translate("Sat")];
    const byday = Array(7).fill(0);
    
    // Count by hour
    const byHour = Array(24).fill(0);

    validTimestamps.forEach(date => {
      byday[date.getDay()]++;
      byHour[date.getHours()]++;
    });

    // Find max for scaling
    const maxDay = Math.max(...byday);
    const maxHour = Math.max(...byHour);

    // Find peak times
    const peakDayIdx = byday.indexOf(maxDay);
    const peakHourIdx = byHour.indexOf(maxHour);

    // Format data
    const byDayData = byday.map((count, idx) => ({
      day: dayNames[idx],
      dayShort: dayShort[idx],
      count,
      percentage: maxDay > 0 ? Math.round((count / maxDay) * 100) : 0,
      isWeekend: idx === 0 || idx === 6,
    }));

    const byHourData = byHour.map((count, idx) => ({
      hour: idx,
      hourLabel: formatHour(idx),
      count,
      percentage: maxHour > 0 ? Math.round((count / maxHour) * 100) : 0,
      period: getTimePeriod(idx),
    }));

    return {
      byDay: byDayData,
      byHour: byHourData,
      peakDay: dayNames[peakDayIdx],
      peakHour: formatHour(peakHourIdx),
      totalEvents: validTimestamps.length,
    };
  }, [sessions, detailedSessions, locale]);

  function formatHour(hour) {
    if (hour === 0) return translate("12 AM");
    if (hour === 12) return translate("12 PM");
    if (hour < 12) return translate("{value0} AM", {
  value0: hour
});
    return translate("{value0} PM", {
  value0: hour - 12
});
  }

  function getTimePeriod(hour) {
    if (hour >= 5 && hour < 12) return "morning";
    if (hour >= 12 && hour < 17) return "afternoon";
    if (hour >= 17 && hour < 21) return "evening";
    return "night";
  }

  if (timeAnalysis.totalEvents === 0) {
    return (
      <div className="activityTime">
        <div className="atEmpty">
          <div className="atEmptyIcon">🕐</div>
          <div className="atEmptyText">{translate("No activity data available")}</div>
          <div className="atEmptySub">{translate("Activity patterns will appear as students use the platform")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="activityTime">
      {/* View toggle */}
      <div className="atHeader">
        <div className="atToggle">
          <AppButton
            type="button"
            kind={view === "day" ? "primary" : "ghost"}
            className={`atToggleBtn ${view === "day" ? "active" : ""}`}
            onClick={() => setView("day")}
          >{translate("By Day")}</AppButton>
          <AppButton
            type="button"
            kind={view === "hour" ? "primary" : "ghost"}
            className={`atToggleBtn ${view === "hour" ? "active" : ""}`}
            onClick={() => setView("hour")}
          >{translate("By Hour")}</AppButton>
        </div>
        <div className="atPeak">
          {view === "day" ? (
            <>{translateRich("Peak day: {value0}", { value0: <strong>{timeAnalysis.peakDay}</strong> })}</>
          ) : (
            <>{translateRich("Peak hour: {value0}", { value0: <strong>{timeAnalysis.peakHour}</strong> })}</>
          )}
        </div>
      </div>

      {/* By Day View */}
      {view === "day" && (
        <div className="atDayView">
          <div className="atDayGrid">
            {timeAnalysis.byDay.map((day) => (
              <div 
                key={day.day} 
                className={`atDayBar ${day.isWeekend ? "atDayBar--weekend" : ""}`}
              >
                <div className="atDayBarWrapper">
                  <div 
                    className="atDayBarFill"
                    style={{ height: `${day.percentage}%` }}
                  />
                </div>
                <div className="atDayLabel">{day.dayShort}</div>
                <div className="atDayCount">{day.count}</div>
              </div>
            ))}
          </div>
          
          {/* Day insights */}
          <div className="atInsights">
            {(() => {
              const weekdayTotal = timeAnalysis.byDay
                .filter(d => !d.isWeekend)
                .reduce((sum, d) => sum + d.count, 0);
              const weekendTotal = timeAnalysis.byDay
                .filter(d => d.isWeekend)
                .reduce((sum, d) => sum + d.count, 0);
              const weekdayPct = Math.round((weekdayTotal / (weekdayTotal + weekendTotal)) * 100);
              
              return (
                <div className="atInsight">{translateRich("\uD83D\uDCC5 {value0} of activity happens on weekdays", {
  value0: <strong>{weekdayPct}%</strong>
})}</div>
              );
            })()}
          </div>
        </div>
      )}

      {/* By Hour View */}
      {view === "hour" && (
        <div className="atHourView">
          <div className="atHourGrid">
            {timeAnalysis.byHour.map((hour) => (
              <div 
                key={hour.hour} 
                className={`atHourBar atHourBar--${hour.period}`}
                title={translate("{value0}: {value1} events", {
  value0: hour.hourLabel,
  value1: hour.count
})}
              >
                <div 
                  className="atHourBarFill"
                  style={{ height: `${hour.percentage}%` }}
                />
              </div>
            ))}
          </div>
          
          {/* Hour labels */}
          <div className="atHourLabels">
            <span>{translate("12AM")}</span>
            <span>{translate("6AM")}</span>
            <span>{translate("12PM")}</span>
            <span>{translate("6PM")}</span>
            <span>{translate("12AM")}</span>
          </div>

          {/* Hour insights */}
          <div className="atInsights">
            {(() => {
              const morningTotal = timeAnalysis.byHour
                .filter(h => h.period === "morning")
                .reduce((sum, h) => sum + h.count, 0);
              const afternoonTotal = timeAnalysis.byHour
                .filter(h => h.period === "afternoon")
                .reduce((sum, h) => sum + h.count, 0);
              const eveningTotal = timeAnalysis.byHour
                .filter(h => h.period === "evening")
                .reduce((sum, h) => sum + h.count, 0);
              
              const periods = [
                { name: translate("morning (5AM-12PM)"), total: morningTotal },
                { name: translate("afternoon (12PM-5PM)"), total: afternoonTotal },
                { name: translate("evening (5PM-9PM)"), total: eveningTotal },
              ].sort((a, b) => b.total - a.total);
              
              return (
                <div className="atInsight">{translateRich("\u23F0 Most activity happens in the {value0}", {
  value0: <strong>{periods[0].name}</strong>
})}</div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Legend for hour view */}
      {view === "hour" && (
        <div className="atLegend">
          <span className="atLegendItem">{translateRich("{value0} Morning", {
  value0: <span className="atLegendDot atLegendDot--morning" />
})}</span>
          <span className="atLegendItem">{translateRich("{value0} Afternoon", {
  value0: <span className="atLegendDot atLegendDot--afternoon" />
})}</span>
          <span className="atLegendItem">{translateRich("{value0} Evening", {
  value0: <span className="atLegendDot atLegendDot--evening" />
})}</span>
          <span className="atLegendItem">{translateRich("{value0} Night", {
  value0: <span className="atLegendDot atLegendDot--night" />
})}</span>
        </div>
      )}
    </div>
  );
}
